import { useRef, useState, useCallback, useEffect } from "react";
import { getAccessToken, tryRestoreSession } from "@/lib/api";

const WS_BASE = import.meta.env.VITE_WS_URL ?? "";

type ConciergeMessage =
  | { type: "text_delta"; content: string }
  | { type: "text_done"; content: string }
  | { type: "greeting"; content: string }
  | { type: "widget"; widget_type: string; data: Record<string, unknown>; actions?: Array<{ id: string; label: string; style?: string }> }
  | { type: "route_to_project"; project_id: string; project_name: string; forward_prompt?: string }
  | { type: "error"; message: string }
  | { type: "pong" };

interface UseConciergeOptions {
  onMessage?: (msg: ConciergeMessage) => void;
}

type ConnectionState = "connecting" | "connected" | "disconnected";

export function useConcierge({ onMessage }: UseConciergeOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<ConnectionState>("disconnected");
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    const base = WS_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/, "ws");
    const url = `${wsUrl}/ws/concierge`;

    setState("connecting");
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: "auth",
        token,
        client_hour: new Date().getHours(),
      }));
      setState("connected");
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as ConciergeMessage;
        onMessageRef.current?.(msg);
      } catch (_err) {
        console.error("[concierge] Failed to parse message:", event.data);
      }
    };

    ws.onclose = (event) => {
      setState("disconnected");
      wsRef.current = null;

      if (event.code === 4003 || event.code === 4001) {
        tryRestoreSession().then((restored) => {
          if (restored) {
            reconnectTimer.current = setTimeout(connect, 500);
          }
        });
        return;
      }

      reconnectTimer.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => {};
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    setState("disconnected");
  }, []);

  const send = useCallback((content: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "chat", content }));
    }
  }, []);

  useEffect(() => {
    return () => { disconnect(); };
  }, [disconnect]);

  return { state, connect, disconnect, send };
}
